import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import BillService from '../../services/bill.service';
import { Pill, Section } from '../../components/EntityList';
import { billOwnerAmount } from '../../utils/billPayers';
import { showCurrency } from '../../utils';
import { useLabels } from '../../hooks/useLabels';

const money = (currency, amount) => `${showCurrency(currency) || ''} ${Number(amount || 0).toLocaleString('es-UY')}`.trim();

/**
 * Who covers this bill (SHOW_BILL_PAYERS): each payer's share is a debt on
 * their account, shown here with its payment status; the remainder is the
 * asset owner's cost.
 */
export default function BillPayerShares({ bill, currency }) {
	const { t } = useLabels();
	const [debts, setDebts] = useState([]);

	useEffect(() => {
		let active = true;
		BillService.getPayerDebts(bill._id)
			.then(({ data }) => active && setDebts(data || []))
			.catch(() => active && setDebts([]));
		return () => {
			active = false;
		};
	}, [bill._id, bill.amount, bill.updatedAt]);

	if (!bill.payers?.length && !debts.length) return null;

	const byAccount = new Map(debts.map((d) => [String(d.owner?._id || d.owner), d]));

	return (
		<Section title={t('billPayers.title')} description={t('billPayers.detailDescription')} className='tw:mt-4'>
			<table className='tw:w-full tw:text-[13px]'>
				<thead>
					<tr className='tw:text-left tw:text-[11px] tw:uppercase tw:tracking-[0.08em] tw:text-ink-3'>
						<th className='tw:py-1.5'>{t('entity.account')}</th>
						<th className='tw:py-1.5'>%</th>
						<th className='tw:py-1.5'>{t('field.amount')}</th>
						<th className='tw:py-1.5'>{t('billPayers.status')}</th>
					</tr>
				</thead>
				<tbody>
					{(bill.payers || []).map((payer) => {
						const debt = byAccount.get(String(payer.account?._id || payer.account));
						const due = debt?.generatedDebts?.[0];
						const paid = due?.paid;
						return (
							<tr key={String(payer.account?._id || payer.account)} className='tw:border-t tw:border-border'>
								<td className='tw:py-2'>
									{debt ? <Link to={`/AdminScreen/debts/debt/${debt._id}`}>{debt.owner?.name || '—'}</Link> : '—'}
								</td>
								<td className='tw:py-2 tw:font-mono'>{payer.percentage}%</td>
								<td className='tw:py-2 tw:font-mono'>{money(currency, debt?.amount)}</td>
								<td className='tw:py-2'>
									<Pill tone={paid ? 'ok' : 'warn'} dot>{paid ? t('field.paidFull') : t('field.pendingPayment')}</Pill>
								</td>
							</tr>
						);
					})}
					<tr className='tw:border-t tw:border-border tw:text-ink-2'>
						<td className='tw:py-2'>{t('billPayers.ownerShare')}</td>
						<td className='tw:py-2 tw:font-mono'>
							{Math.max(0, 100 - (bill.payers || []).reduce((s, p) => s + Number(p.percentage || 0), 0))}%
						</td>
						<td className='tw:py-2 tw:font-mono'>{money(currency, billOwnerAmount(bill))}</td>
						<td />
					</tr>
				</tbody>
			</table>
			{debts.filter((d) => !(bill.payers || []).some((p) => String(p.account?._id || p.account) === String(d.owner?._id || d.owner))).length > 0 && (
				<p className='tw:mt-2 tw:mb-0 tw:text-[12px] tw:text-warn'>{t('billPayers.orphanDebts')}</p>
			)}
		</Section>
	);
}

BillPayerShares.propTypes = {
	bill: PropTypes.object.isRequired,
	currency: PropTypes.string,
};
